import React, { useCallback, useRef, useState } from 'react';

// One tooltip for every chart in the quest.
//
// It replaces SVG's own <title>, which is free but waits about a second before appearing, renders
// in the OS's styling rather than the page's, and can't be positioned. On a chart where hovering is
// how you read the numbers, a delay that long reads as nothing happening.
//
// Content is a title plus label/value rows, not prose. Sentences in a narrow card wrap wherever
// they run out of room — "written Apr 16, 2020 · graded Apr" / "16, 2021" — whereas short rows in
// a two-column grid can't: each cell sets its own width and nothing wraps at all. It also lines the
// numbers up, which is what makes two tooltips comparable when you sweep across a series.
//
//   const tip = useChartTooltip();
//   <figure className="pred-chart" ref={tip.ref}>
//     <svg>…<rect {...tip.bind({ title: 'Dan 2023', rows: [['Brier', '0.131']] })} />…</svg>
//     {tip.node}
//   </figure>
//
// Rows with a null/undefined value are dropped, so a caller can list every field it might have.

const CURSOR_OFFSET = 14;
const HALF_WIDTH = 92; // half the tooltip's max width, for keeping it inside the figure
const ROW_H = 17; // only used to guess whether the card fits above the cursor
const CHROME_H = 32;

export function useChartTooltip() {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);

  const track = useCallback((content, event) => {
    const box = ref.current && ref.current.getBoundingClientRect();
    if (!box) return;
    const rows = (content.rows || []).filter(
      ([, value]) => value !== null && value !== undefined && value !== ''
    );
    setTip({
      title: content.title,
      rows,
      x: event.clientX - box.left,
      y: event.clientY - box.top,
      width: box.width,
      // Sitting above the cursor is the default; near the top of a tall card there's no room, so
      // it drops below rather than being clipped by the figure.
      below: event.clientY - box.top < CHROME_H + rows.length * ROW_H + CURSOR_OFFSET
    });
  }, []);

  const bind = useCallback(
    content => ({
      onMouseEnter: event => track(content, event),
      onMouseMove: event => track(content, event),
      onMouseLeave: () => setTip(null),
      // Screen readers get the same content, since the tooltip itself is pointer-only.
      role: 'img',
      'aria-label': [
        content.title,
        ...(content.rows || [])
          .filter(([, value]) => value !== null && value !== undefined && value !== '')
          .map(([label, value]) => `${label} ${value}`)
      ].join('. ')
    }),
    [track]
  );

  const node = tip ? (
    <div
      className={`pred-chart-tip ${tip.below ? 'is-below' : ''}`}
      role="tooltip"
      style={{
        left: Math.min(Math.max(tip.x, HALF_WIDTH), Math.max(HALF_WIDTH, tip.width - HALF_WIDTH)),
        top: tip.y + (tip.below ? CURSOR_OFFSET : -CURSOR_OFFSET)
      }}
    >
      <div className="pred-chart-tip-head">{tip.title}</div>
      {tip.rows.length > 0 && (
        <dl className="pred-chart-tip-rows">
          {tip.rows.map(([label, value]) => (
            <React.Fragment key={label}>
              <dt>{label}</dt>
              <dd>{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      )}
    </div>
  ) : null;

  return { ref, bind, node };
}
